import { EditPathCommand, clonePathModel } from "../commands/EditPathCommand";
import { screenToDoc } from "../render/Viewport";
import type { KeyModifiers, PointerSample, Tool, ToolContext } from "./Tool";
import type { AnchorPoint, PathModel, SubPath } from "../document/PathModel";
import type { ShapeLayer } from "../document/Layer";

export class DirectSelectionTool implements Tool {
  readonly id = "direct-select";
  readonly shortcut = "A";

  private selectedPoint: { subpathIdx: number; pointIdx: number } | null = null;
  private selectedHandle: "left" | "right" | null = null;
  private _isDragging = false;
  private dragStart: { x: number; y: number } | null = null;
  private selectedSubpath: number | null = null;

  // Snapshot of the path taken at pointer-down, for undo
  private pathAtDragStart: PathModel | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    const docPos = screenToDoc(ctx.viewport, s.x, s.y);
    this.dragStart = { x: docPos.x, y: docPos.y };
    this._isDragging = false;

    const activeLayer = ctx.doc?.activeLayerId
      ? this.findLayerById(ctx.doc.layers, ctx.doc.activeLayerId)
      : null;

    if (!activeLayer || activeLayer.kind !== "shape") {
      this.selectedPoint = null;
      this.selectedHandle = null;
      this.selectedSubpath = null;
      this.pathAtDragStart = null;
      ctx.requestRender();
      return;
    }

    const shape = activeLayer as ShapeLayer;
    const hit = this.hitTest(shape, docPos.x, docPos.y);

    if (!hit) {
      this.selectedPoint = null;
      this.selectedHandle = null;
      this.selectedSubpath = null;
      this.pathAtDragStart = null;
      ctx.requestRender();
      return;
    }

    if (hit.type === "point") {
      this.selectedPoint = { subpathIdx: hit.subpathIdx, pointIdx: hit.pointIdx };
      this.selectedHandle = null;
      this.selectedSubpath = hit.subpathIdx;
    } else if (hit.type === "handle") {
      this.selectedPoint = { subpathIdx: hit.subpathIdx, pointIdx: hit.pointIdx };
      this.selectedHandle = hit.handle ?? null;
      this.selectedSubpath = hit.subpathIdx;
    } else if (hit.type === "segment") {
      this.selectedSubpath = hit.subpathIdx;
      this.selectedPoint = null;
      this.selectedHandle = null;
    } else {
      this.selectedPoint = null;
      this.selectedHandle = null;
      this.selectedSubpath = null;
    }

    // Snapshot path before any drag begins (used to build the EditPathCommand on pointer-up)
    this.pathAtDragStart = clonePathModel(shape.path);

    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    const docPos = screenToDoc(ctx.viewport, s.x, s.y);

    if (!this.selectedPoint || !this.dragStart) {
      ctx.requestRender();
      return;
    }

    const activeLayer = ctx.doc?.activeLayerId
      ? this.findLayerById(ctx.doc.layers, ctx.doc.activeLayerId)
      : null;

    if (!activeLayer || activeLayer.kind !== "shape") return;

    const shape = activeLayer as ShapeLayer;
    const subpath = shape.path.subpaths[this.selectedPoint.subpathIdx];
    if (!subpath) return;

    const pt = subpath.points[this.selectedPoint.pointIdx];
    if (!pt) return;

    const dx = docPos.x - this.dragStart.x;
    const dy = docPos.y - this.dragStart.y;

    if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
      this._isDragging = true;
    }

    // Transient preview: mutate live path for visual feedback
    if (this.selectedHandle === "left" && pt.leftDirection) {
      pt.leftDirection.x += dx;
      pt.leftDirection.y += dy;
      if (!s.altKey && pt.rightDirection) {
        pt.rightDirection.x -= dx;
        pt.rightDirection.y -= dy;
      }
    } else if (this.selectedHandle === "right" && pt.rightDirection) {
      pt.rightDirection.x += dx;
      pt.rightDirection.y += dy;
      if (!s.altKey && pt.leftDirection) {
        pt.leftDirection.x -= dx;
        pt.leftDirection.y -= dy;
      }
    } else if (!this.selectedHandle) {
      pt.x += dx;
      pt.y += dy;
      if (pt.leftDirection) {
        pt.leftDirection.x += dx;
        pt.leftDirection.y += dy;
      }
      if (pt.rightDirection) {
        pt.rightDirection.x += dx;
        pt.rightDirection.y += dy;
      }
    }

    this.dragStart = { x: docPos.x, y: docPos.y };
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext, _s: PointerSample): void {
    const wasDragging = this._isDragging;
    this._isDragging = false;
    this.dragStart = null;

    if (wasDragging && this.pathAtDragStart && ctx.doc?.activeLayerId) {
      const activeLayer = this.findLayerById(ctx.doc.layers, ctx.doc.activeLayerId);
      if (activeLayer?.kind === "shape") {
        const shape = activeLayer as ShapeLayer;
        // Emit command: path changed from snapshot → current live path
        const pathAfter = clonePathModel(shape.path);
        ctx.emit(new EditPathCommand(ctx.doc.activeLayerId, this.pathAtDragStart, pathAfter));
      }
    }

    this.pathAtDragStart = null;
  }

  onKeyDown(ctx: ToolContext, key: string, modifiers?: KeyModifiers): void {
    if (key === "Delete" || key === "Backspace") {
      if (!this.selectedPoint) return;
      const activeLayer = ctx.doc?.activeLayerId
        ? this.findLayerById(ctx.doc.layers, ctx.doc.activeLayerId)
        : null;
      if (!activeLayer || activeLayer.kind !== "shape") return;

      const shape = activeLayer as ShapeLayer;
      const subpath = shape.path.subpaths[this.selectedPoint.subpathIdx];
      if (!subpath || subpath.points.length <= 2) return;

      // Snapshot before deletion
      const pathBefore = clonePathModel(shape.path);

      // Apply deletion to the live path (transient mutation)
      subpath.points.splice(this.selectedPoint.pointIdx, 1);
      const selectedPoint = this.selectedPoint;
      this.selectedPoint = null;
      this.selectedHandle = null;

      // Build after-path from current state
      const pathAfter = clonePathModel(shape.path);

      // Emit undoable command
      if (!ctx.doc) return;
      ctx.emit(new EditPathCommand(ctx.doc.activeLayerId!, pathBefore, pathAfter));

      // Restore live path to after-state (EditPathCommand.apply will set it when History runs it)
      // But since this is an imperative mutation, we need to keep it consistent.
      // The emit() will call apply() immediately via History, which writes pathAfter back.
      // However we already mutated the live layer. The apply() call in History will
      // produce a new doc with pathAfter set — that will match. No double-mutation since
      // EditPathCommand.apply uses setPath which creates a NEW layer object.
      // The live mutation above (splice) is effectively overwritten by the command's apply.
      void selectedPoint;
      ctx.requestRender();
    }
  }

  getSelection(): { subpathIdx: number | null; pointIdx: number | null; handle: string | null } {
    return {
      subpathIdx: this.selectedSubpath,
      pointIdx: this.selectedPoint?.pointIdx ?? null,
      handle: this.selectedHandle,
    };
  }

  clearSelection(): void {
    this.selectedPoint = null;
    this.selectedHandle = null;
    this.selectedSubpath = null;
    this.pathAtDragStart = null;
  }

  private hitTest(
    shape: ShapeLayer,
    x: number,
    y: number,
  ): { type: "point" | "handle" | "segment"; subpathIdx: number; pointIdx: number; handle?: "left" | "right" } | null {
    const POINT_RADIUS = 6;
    const HANDLE_RADIUS = 5;

    for (let si = 0; si < shape.path.subpaths.length; si++) {
      const subpath = shape.path.subpaths[si]!;
      for (let pi = 0; pi < subpath.points.length; pi++) {
        const pt = subpath.points[pi]!;

        if (pt.leftDirection) {
          const dist = Math.hypot(x - pt.leftDirection.x, y - pt.leftDirection.y);
          if (dist < HANDLE_RADIUS) {
            return { type: "handle", subpathIdx: si, pointIdx: pi, handle: "left" };
          }
        }

        if (pt.rightDirection) {
          const dist = Math.hypot(x - pt.rightDirection.x, y - pt.rightDirection.y);
          if (dist < HANDLE_RADIUS) {
            return { type: "handle", subpathIdx: si, pointIdx: pi, handle: "right" };
          }
        }

        const dist = Math.hypot(x - pt.x, y - pt.y);
        if (dist < POINT_RADIUS) {
          return { type: "point", subpathIdx: si, pointIdx: pi };
        }
      }
    }

    return null;
  }

  private findLayerById(layers: readonly import("../document/Layer").Layer[], id: string): import("../document/Layer").Layer | null {
    for (const layer of layers) {
      if (layer.id === id) return layer;
      if (layer.kind === "group") {
        const found = this.findLayerById(layer.children, id);
        if (found) return found;
      }
    }
    return null;
  }
}
